import * as _ from "lodash";
import React, { useState } from "react";
import Tooltip from "react-tooltip";

import { CHEVRON_DOWN, CHEVRON_RIGHT, EDIT, INFO } from "../../../Constants";
import {
    getHeaderTitles,
    getEditableItemGroupColumnClass,
} from "../../../Helpers/table";
import { selectLink, linkToRelativeUrl } from "../../../Helpers/links";
import * as Models from "../../../Models";
import {
    AttributeCell,
    AttributeMetadata,
    AttributeRow,
    EditableItemField,
} from "../../../Models";
import { OutotecIcons } from "../../General/Icons/";
import DataGrid from "../DataGrid";
// import SideNavModal from "../../Layout/SideNav/SideNavModal";

const configurationInfoText =
    "Data coming from SAP. This data is referencing the sales configuration. The real configuration can change over time but is not reflected in this data. This data is not modifiable. Exporting this data is not supported through IB Portal, but can be done through Salesforce or PowerBI.";

export default function EditableItemGroup(
    props: Models.EditableItemGroupProps
) {
    const [selectedGroup, setSelectedGroup] = useState<string>("");

    const isColumnEdited: Function = (
        editableField: EditableItemField
    ): boolean => {
        return (
            editableField &&
            editableField.fieldOldValue !== editableField.fieldNewValue
        );
    };

    const getDescription: Function = (
        column: AttributeCell,
        editableField: EditableItemField
    ): string => {
        return !editableField || !isColumnEdited(editableField)
            ? column?.description
            : editableField.fieldNewValue;
    };

    const getDescriptionElement: Function = (
        column: AttributeCell,
        editableField: EditableItemField
    ): JSX.Element => {
        let description: string = getDescription(column, editableField);
        const isDisabled: boolean =
            description.toLowerCase() === "inactive" ||
            description.toLowerCase() === "closed";

        // Prevents long description from floating over edit icon
        if (
            description.length > 40 &&
            (!editableField || (editableField && !editableField.inEditMode))
        ) {
            description = description.substring(0, 40);
        }

        let isEdited: boolean = false;
        let classes: string = "";

        if (editableField && isColumnEdited(editableField)) {
            isEdited = true;
            classes +=
                "ItemGroup__cellText ItemGroup__cellText--editable ItemGroup__cellText--edited";
        } else {
            classes += "ItemGroup__fieldChangedText";
        }

        if (isDisabled) {
            classes += " inactive";
        }

        if (editableField && editableField.inEditMode) {
            return <span />;
        }

        return (
            <span title={description} className={classes}>
                {description}
            </span>
        );
    };

    // const handleKeyPress: Function = (
    //     e: any,
    //     fieldId: string,
    //     editableField: EditableItemField
    // ) => {
    //     if (e.key === "Enter") {
    //         submitFieldChange(e, fieldId, editableField);
    //     }
    // };

    // const handleOnBlur: Function = (
    //     e: any,
    //     fieldId: string,
    //     editableField: EditableItemField
    // ) => {
    //     submitFieldChange(e, fieldId, editableField);
    // };

    // const handleOnAcceptModalItem: Function = (
    //     newValue: string,
    //     editableField: EditableItemField
    // ) => {
    //     editableField.fieldNewValue = newValue;
    //     editableField.inEditMode = false;
    //     props.onChangeFieldValue(editableField);
    // };

    // const handleOnCancelLocationChange: Function = (
    //     fieldId: string,
    //     editableField: EditableItemField
    // ) => {
    //     cancelLocationChange(fieldId, editableField);
    // };

    // const handleDatepickerOnChange: Function = (
    //     date: moment.Moment,
    //     fieldId: string,
    //     editableField: EditableItemField
    // ) => {
    //     if (date && moment.isMoment(date)) {
    //         editableField.fieldNewValue = date.format("M/D/YYYY");
    //     }
    //     editableField.inEditMode = false;
    //     props.onChangeFieldValue(editableField);
    // };

    const handleEdit: Function = (
        fieldId: string,
        description: string,
        editableField: EditableItemField
    ) => {
        if (!editableField) {
            const newField: EditableItemField = {
                inEditMode: true,
                siteId: "",
                objectId: "",
                fieldId: fieldId,
                fieldOldValue: description,
                dataGroupName: props.dataGroupName,
                itemGroupName: props.attributeGroup.name,
                fieldType: 1,
                fieldNewValue: description,
                required: false,
            };
            props.onChangeFieldValue(newField);
        } else {
            editableField.inEditMode = true;
            props.onChangeFieldValue(editableField);
        }
    };

    // const cancelLocationChange: Function = (
    //     fieldId: string,
    //     editableField: EditableItemField
    // ) => {
    //     editableField.inEditMode = false;
    //     props.onChangeFieldValue(editableField);
    //     props.onClearSelectedModalNavigationItem();
    // };

    // const submitFieldChange: Function = (
    //     e: any,
    //     fieldId: string,
    //     editableField: EditableItemField
    // ) => {
    //     const newValue: string = e.target.value.trim();
    //     editableField.fieldNewValue = newValue;
    //     editableField.inEditMode = false;
    //     props.onChangeFieldValue(editableField);
    // };
    const attributeRuleMap: Object = _.keyBy(props.attributeMap, (a) =>
        a.attribute.toLocaleLowerCase()
    );

    const dataGridContent: Models.DataGridContent = {
        columns: [],
        data: [],
    };

    const getFormattedToolTip: Function = (toolTip: string) => {
        const arr: string[] = toolTip.split(";");
        let formattedToolTip: string = "";

        if (arr.length > 0) {
            formattedToolTip += `<ul class='ToolTip__disc'>`;
            arr.forEach((line) => {
                formattedToolTip += `<li>${line}</li>`;
            });
            formattedToolTip += `</ul>`;
        }

        return formattedToolTip;
    };

    let headerTitles: any[] = getHeaderTitles(
        props.attributeGroup.headers,
        selectedGroup
            ? props?.attributeGroup?.rows.filter(
                  (r) => r?.group === selectedGroup
              )
            : props?.attributeGroup?.rows
    );

    dataGridContent.columns = headerTitles.map(
        (h: { headerName: string; headerAccessor: string }) => {
            return {
                Header: h.headerName,
                accessor: h.headerAccessor,
                filterMethod: (filter, row) => {
                    const col: any = row[filter.id];
                    const colDescription: string = col?.description
                        ? col?.description
                        : col.url
                        ? col.url?.description
                        : "";
                    if (colDescription === null) {
                        return false;
                    }
                    return col
                        ? colDescription
                              .toLowerCase()
                              .indexOf(filter.value.toLowerCase()) > -1
                        : false;
                },
                getProps: (state, rowInfo, column) => {
                    return {
                        className: getEditableItemGroupColumnClass(
                            column,
                            rowInfo
                        ),
                    };
                },
                Cell: (cell: any) => {
                    const attributeCell: AttributeCell = cell.value;
                    if (!attributeCell) {
                        return "";
                    }

                    const columnHeaderId: string = cell.column.id;
                    const firstColumnHeaderId: any =
                        headerTitles[0].headerAccessor;
                    const fieldId: string =
                        cell.row[firstColumnHeaderId]?.description;
                    const cellValue: string = cell.value?.description;
                    const firstIndex: boolean =
                        cellValue === fieldId &&
                        columnHeaderId === firstColumnHeaderId;
                    const description: string =
                        attributeCell.url && attributeCell.url?.description
                            ? attributeCell.url?.description
                            : attributeCell?.description;

                    if (firstIndex) {
                        return attributeCell.url && attributeCell.url.id ? (
                            <a
                                href={linkToRelativeUrl(attributeCell.url)}
                                onClick={(e) => {
                                    selectLink(e, attributeCell.url);
                                }}
                            >
                                {description}
                            </a>
                        ) : (
                            <span
                                className="ItemGroup__cell"
                                title={description}
                            >
                                {description}
                            </span>
                        );
                    }

                    if (!firstIndex) {
                        return attributeCell.url && attributeCell.url.id ? (
                            <a
                                href={linkToRelativeUrl(attributeCell.url)}
                                onClick={(e) => {
                                    selectLink(e, attributeCell.url);
                                }}
                            >
                                {description}
                            </a>
                        ) : attributeCell.toolTip ? (
                            <span
                                className="ItemGroup__fieldChanged"
                                data-for={`react_tooltip_${attributeCell?.description}`}
                                data-tip={`<div>${getFormattedToolTip(
                                    attributeCell.toolTip
                                )}</div>`}
                                data-html
                            >
                                {description} <OutotecIcons type={INFO} />
                                <Tooltip
                                    class="Tooltip Tooltip--outotec Tooltip--fieldChange"
                                    id={`react_tooltip_${attributeCell?.description}`}
                                    place="right"
                                    html={true}
                                    effect="solid"
                                />
                            </span>
                        ) : (
                            <span
                                className={`ItemGroup__cell`}
                                title={description}
                            >
                                {getDescriptionElement(
                                    attributeCell,
                                    null
                                    // props.isEditMode
                                )}
                            </span>
                        );
                    }

                    const editableField: EditableItemField =
                        props.editedItemFields.find(
                            (i) => i.fieldId === fieldId
                        );
                    const attributeRules: AttributeMetadata = attributeRuleMap[
                        fieldId.toLowerCase()
                    ]
                        ? attributeRuleMap[fieldId.toLowerCase()]
                        : {};

                    const columnClassNames: string = isColumnEdited(
                        editableField
                    )
                        ? "ItemGroup__cell--edited"
                        : "";
                    const location: Models.Location = {
                        pathname: "",
                        hash: "",
                        search: "",
                    };

                    return (
                        <span
                            className={`ItemGroup__cell ItemGroup__cell--editable ${columnClassNames}`}
                        >
                            <span className="ItemGroup__DescriptionContainer">
                                {getDescriptionElement(
                                    attributeCell,
                                    editableField
                                    // props.isEditMode
                                )}

                                {(!editableField ||
                                    !editableField.inEditMode) && (
                                    <span
                                        className="ItemGroup__editButton"
                                        onClick={(e) =>
                                            handleEdit(
                                                fieldId,
                                                attributeCell?.description,
                                                editableField
                                            )
                                        }
                                    >
                                        <OutotecIcons type={EDIT} />
                                    </span>
                                )}
                            </span>

                            {/* THIS SEEMS UNUSED CAN BE CHECKED LATER{editableField &&
                                editableField.inEditMode &&
                                editableField.fieldId === "Location" && (
                                    <Modal
                                        name="locationsearch"
                                        show={editableField.inEditMode}
                                        shouldCloseOnOverlayClick={false}
                                    >
                                        <SideNavModal
                                            match={props.match}
                                            location={location}
                                            editableField={editableField}
                                        />
                                        {props.selectedModalNavigationItem && (
                                            <div className="SideNavModal__container__selecteditem">
                                                <h3>
                                                    Selected:{" "}
                                                    {
                                                        props
                                                            .selectedModalNavigationItem
                                                            ?.description
                                                    }
                                                    (
                                                    {
                                                        props
                                                            .selectedModalNavigationItem
                                                            .name
                                                    }
                                                    )
                                                </h3>
                                            </div>
                                        )}
                                        <div className="SideNavModal__button">
                                            <button
                                                onClick={(e) =>
                                                    handleOnCancelLocationChange(
                                                        fieldId,
                                                        editableField
                                                    )
                                                }
                                                className="btn btn-primary"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        {props.selectedModalNavigationItem && (
                                            <div className="SideNavModal__button__accept">
                                                <button
                                                    onClick={(e) =>
                                                        handleOnAcceptModalItem(
                                                            `${props.selectedModalNavigationItem.description}(${props.selectedModalNavigationItem.name})`,
                                                            editableField
                                                        )
                                                    }
                                                    className="btn btn-primary"
                                                >
                                                    Accept
                                                </button>
                                            </div>
                                        )}
                                    </Modal>
                                )}

                            {editableField &&
                                editableField.inEditMode &&
                                editableField.fieldId !== "Location" && (
                                    <span className="ItemGroup__inputContainer">
                                        {attributeRules.type === "Date" && (
                                            <DatePicker
                                                autoFocus
                                                showYearDropdown={true}
                                                scrollableYearDropdown={true}
                                                yearDropdownItemNumber={30}
                                                dateFormat="M/D/YYYY"
                                                onBlur={(date: moment.Moment) =>
                                                    handleDatepickerOnChange(
                                                        date,
                                                        fieldId,
                                                        editableField
                                                    )
                                                }
                                                onChange={(
                                                    date: moment.Moment
                                                ) =>
                                                    handleDatepickerOnChange(
                                                        date,
                                                        fieldId,
                                                        editableField
                                                    )
                                                }
                                                locale="en-gb"
                                                class="form-control"
                                                selected={
                                                    getDescription(
                                                        attributeCell,
                                                        editableField
                                                    ) !== ""
                                                        ? moment(
                                                              getDescription(
                                                                  attributeCell,
                                                                  editableField
                                                              )
                                                          )
                                                        : ""
                                                }
                                                placeholder="Select a date"
                                            />
                                        )}

                                        {attributeRules.type === "Multiple" && (
                                            <select
                                                autoFocus
                                                className="form-control"
                                                onChange={(e: any) =>
                                                    handleOnBlur(
                                                        e,
                                                        fieldId,
                                                        editableField
                                                    )
                                                }
                                                onBlur={(e: any) =>
                                                    handleOnBlur(
                                                        e,
                                                        fieldId,
                                                        editableField
                                                    )
                                                }
                                                defaultValue={getDescription(
                                                    attributeCell,
                                                    editableField
                                                )}
                                            >
                                                <option value="">
                                                    Choose...
                                                </option>
                                                {attributeRules.options.map(
                                                    (o: string) => (
                                                        <option
                                                            key={o}
                                                            value={o}
                                                        >
                                                            {o}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        )}

                                        {attributeRules.type !== "Date" &&
                                            attributeRules.type !==
                                                "Multiple" && (
                                                <input
                                                    autoFocus
                                                    onFocus={(e) =>
                                                        moveCaretAtEnd(e)
                                                    }
                                                    className="form-control"
                                                    onKeyPress={(e: any) =>
                                                        handleKeyPress(
                                                            e,
                                                            fieldId,
                                                            editableField
                                                        )
                                                    }
                                                    onBlur={(e: any) =>
                                                        handleOnBlur(
                                                            e,
                                                            fieldId,
                                                            editableField
                                                        )
                                                    }
                                                    defaultValue={
                                                        editableField.fieldNewValue
                                                    }
                                                />
                                            )}

                                        {attributeRules.infoText && (
                                            <span
                                                className="ItemGroup__info"
                                                data-tip={
                                                    attributeRules.infoText
                                                }
                                            >
                                                <OutotecIcons type={INFO} />
                                                <Tooltip
                                                    class="Tooltip Tooltip--outotec"
                                                    place="right"
                                                    multiline={true}
                                                    effect="solid"
                                                />
                                            </span>
                                        )}
                                    </span>
                                )} */}
                        </span>
                    );
                },
            };
        }
    );

    dataGridContent.data = _.map(
        selectedGroup
            ? props?.attributeGroup?.rows?.filter(
                  (r) => r.group === selectedGroup
              )
            : props?.attributeGroup?.rows,
        (r) => {
            const items: AttributeCell[] = r.items;
            const row: any = {};

            _.forEach(
                headerTitles,
                (
                    h: { headerName: string; headerAccessor: string },
                    i: number
                ) => {
                    row[h.headerAccessor] = items[i];
                }
            );

            return row;
        }
    );

    if (props.isConfigurationTab) {
        const groupsMap: { [groupName: string]: Models.AttributeRow[] } = {};
        props.attributeGroup.rows.forEach((row) => {
            const groupName = row.group;
            if (groupName === undefined) return; //to remove Group headers created in rows in TabbedItemGroup
            if (!groupsMap[groupName]) groupsMap[groupName] = [];
            groupsMap[groupName].push(row);
        });

        const [expandedGroups, setExpandedGroups] = useState<{
            [key: string]: boolean;
        }>(() => {
            const initial: { [key: string]: boolean } = {};
            Object.keys(groupsMap).forEach((g) => (initial[g] = false)); // all collapsed initially
            return initial;
        });

        const toggleGroup = (groupName: string) => {
            setExpandedGroups((prev) => ({
                // ...prev, to allow expanding more than one row
                [groupName]: !prev[groupName],
            }));
            if (selectedGroup !== groupName) setSelectedGroup(groupName);
        };
        return (
            <div className="ItemGroup">
                <h3 className="ItemGroup__title">
                    {props.attributeGroup.name}
                </h3>
                <div style={{ display: "flex" }}>
                    <OutotecIcons type={INFO} classNames="GroupCollapseIcon" />
                    <p
                        style={{
                            fontSize: 12.8,
                            marginLeft: 10,
                            fontFamily: "'robotoregular', Arial, sans-serif",
                        }}
                    >
                        {configurationInfoText}
                    </p>
                </div>

                {Object.keys(groupsMap).map((groupName) => (
                    <div key={groupName}>
                        <div
                            onClick={() => toggleGroup(groupName)}
                            style={{
                                cursor: "pointer",
                                width: "fit-content",
                            }}
                        >
                            {expandedGroups[groupName] ? (
                                <OutotecIcons
                                    type={CHEVRON_DOWN}
                                    classNames="GroupCollapseIcon"
                                />
                            ) : (
                                <OutotecIcons
                                    type={CHEVRON_RIGHT}
                                    classNames="GroupCollapseIcon"
                                />
                            )}{" "}
                            <span
                                style={{
                                    fontFamily:
                                        "'robotoregular', Arial, sans-serif",
                                    fontSize: 12.8,
                                }}
                            >
                                {groupName}
                            </span>
                        </div>

                        {expandedGroups[groupName] && (
                            <DataGrid
                                filterable={props.isInstalledBaseSection}
                                showPagination={false}
                                content={dataGridContent}
                                isSortable={false}
                            />
                        )}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="ItemGroup">
            <h3 className="ItemGroup__title">{props.attributeGroup.name}</h3>

            <DataGrid
                filterable={props.isInstalledBaseSection}
                showPagination={false}
                content={dataGridContent}
                isSortable={false}
            />
        </div>
    );
}
